import { CheckIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { useTranslation } from "react-i18next";

import { ReturnDetail } from "../../models/returns/IReturnsApi";
import formatDateTime from "../../utils/formatDateTime";
import { INBOX_DAY_TIME } from "../../utils/inboxDates";
import PriceFormat from "../../utils/priceFormat";
import { reasonGroupLabel } from "../../utils/returnsRules";
import { CHANNEL_NAMES } from "./ChannelTag";
import StateChip from "./StateChip";

/** The amber card at the top of a return: reason, the buyer's words and photos, and eBay's figures and rules. */
export default function ReturnCard({ detail }: { detail: ReturnDetail }) {
  const { t } = useTranslation("returns");
  const isEbay = detail.channel === "ebay";
  const refund = detail.estimatedRefund;
  const policy = detail.policy;
  const notAsDescribed = detail.reasonGroup === "Not as described";
  const sellerPays = detail.returnPostagePayer === "seller";
  const openedAt = detail.timeline.find((e) => e.toState === "RETURN_REQUESTED")?.at ?? detail.timeline[0]?.at ?? null;

  return (
    <article className="overflow-hidden rounded-xl border border-amber-200 bg-white">
      <div className="flex items-start justify-between gap-3 border-b border-amber-200 bg-amber-50 px-4 py-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-amber-800">
            {isEbay ? t("eBay return request") : t("{{channel}} return", { channel: CHANNEL_NAMES[detail.channel] })} · {reasonGroupLabel(detail.reasonGroup)}
          </span>
          <span className="text-[20px] font-bold leading-tight text-amber-900">{detail.reasonLabel}</span>
        </div>
        <div className="flex flex-col items-end gap-1 text-right text-[11px] text-amber-800">
          <StateChip state={detail.ebayState} label={detail.ebayStateLabel} className="!border !border-amber-200 !bg-white !text-amber-900" />
          <span>
            {detail.ebayReturnId ? `${t("Return ID")} ${detail.ebayReturnId}` : t("Logged in Wilma")}
            {openedAt ? ` · ${t("opened {{when}}", { when: formatDateTime(openedAt, INBOX_DAY_TIME) })}` : ""}
          </span>
        </div>
      </div>
      <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))" }}>
        <div className="flex flex-col gap-2.5 border-r border-gray-100 px-4 py-3">
          {detail.buyerComment ? (
            <blockquote className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-[13px] leading-relaxed text-gray-900">
              "{detail.buyerComment}"
              <footer className="mt-1.5 text-[11px] text-gray-500">
                {detail.buyer.username} · {t("with the request")}
              </footer>
            </blockquote>
          ) : (
            <p className="text-[12px] text-gray-500">{t("The buyer didn't add a comment.")}</p>
          )}
          {detail.buyerPhotos.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{t("Buyer's photos · {{count}}", { count: detail.buyerPhotos.length })}</span>
              <div className="flex flex-wrap gap-2">
                {detail.buyerPhotos.map((p, i) => (
                  <a key={p.url} href={p.url} target="_blank" rel="noreferrer" aria-label={t("Buyer photo {{n}}, enlarge", { n: i + 1 })} className="block h-[70px] w-[92px] overflow-hidden rounded-md border border-gray-200 bg-gray-200">
                    <img loading="lazy" src={p.url} alt="" className="h-full w-full object-cover" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
        <dl className="grid content-start grid-cols-[150px_minmax(0,1fr)] gap-x-2.5 gap-y-[7px] px-4 py-3 text-[12px]">
          <dt className="text-gray-500">{isEbay ? t("eBay's estimated refund") : t("Refund if accepted")}</dt>
          <dd>
            {refund ? (
              <>
                <strong className="text-[14px] font-bold">{PriceFormat(refund.total)}</strong>
                {refund.item !== null && refund.postage !== null && (
                  <>
                    {" "}
                    <span className="text-gray-500">· {t("{{item}} item + {{postage}} original postage", { item: PriceFormat(refund.item), postage: PriceFormat(refund.postage) })}</span>
                  </>
                )}
              </>
            ) : (
              <span className="text-gray-500">{t("Not known yet")}</span>
            )}
          </dd>
          <dt className="text-gray-500">{t("Return postage")}</dt>
          <dd>
            {detail.returnPostagePayer ? (
              <>
                <strong className="font-semibold">{sellerPays ? t("You pay") : t("Buyer pays")}</strong>
                {detail.returnPostageWhy && <span className="text-gray-500"> · {detail.returnPostageWhy}</span>}
              </>
            ) : (
              <span className="text-gray-500">{t("Not known")}</span>
            )}
          </dd>
          {isEbay && (
            <>
              <dt className="text-gray-500">{t("Policy on the listing")}</dt>
              <dd>
                {policy ? (
                  <>
                    {policy.returnsAccepted === false
                      ? t("No returns")
                      : t("{{days}} days, {{who}} pays return postage", { days: policy.days ?? "—", who: policy.postagePayer === "seller" ? t("seller") : t("buyer") })}
                    {policy.name && <span className="text-gray-500"> · {t('read from eBay business policy "{{name}}"', { name: policy.name })}</span>}
                  </>
                ) : (
                  <span className="text-gray-500">{t("Not read from eBay yet")}</span>
                )}
              </dd>
              <dt className="text-gray-500">{t("Money Back Guarantee")}</dt>
              <dd className="flex items-center gap-1.5">
                {detail.withinReturnWindow === null ? (
                  <span className="text-gray-500">{t("Not known")}</span>
                ) : detail.withinReturnWindow ? (
                  <>
                    <CheckIcon className="h-4 w-4 text-green-700" aria-label={t("Yes")} />
                    {policy?.days ? t("Inside the {{days}}-day window", { days: policy.days }) : t("Inside the window")}
                    <span className="text-gray-500">· {detail.deliveredAt ? t("delivered {{when}}", { when: formatDateTime(detail.deliveredAt, INBOX_DAY_TIME) }) : t("eBay says inside the window")}</span>
                  </>
                ) : (
                  <>
                    <XMarkIcon className="h-4 w-4 text-red-700" aria-label={t("No")} />
                    {t("Outside the return window")}
                  </>
                )}
              </dd>
              {detail.stage === "Requested" && (
                <>
                  <dt className="text-gray-500">{t("If you don't respond")}</dt>
                  <dd className="text-gray-500">{notAsDescribed ? t("eBay may refund the buyer and charge you, without the item coming back.") : t("eBay may refund the buyer and charge you.")}</dd>
                </>
              )}
            </>
          )}
        </dl>
      </div>
    </article>
  );
}
